import { useEffect, useRef, useState } from 'react';

const CLOSE_DELAY_MS = 200;

export const useHoverTrigger = (triggerId: string) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const ignoreCloseUntilRef = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const suppressCloseFor = (ms: number) => {
    ignoreCloseUntilRef.current = Date.now() + ms;
  };

  const open = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const close = () => {
    if (ignoreCloseUntilRef.current && Date.now() < ignoreCloseUntilRef.current) {
      return;
    }
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => {
    const trigger = document.querySelector<HTMLElement>(`[id="${triggerId}"]`);
    if (!trigger) {
      return;
    }

    const hadTabIndex = trigger.hasAttribute('tabindex');
    const hadRole = trigger.hasAttribute('role');
    const isNaturallyFocusable = trigger.matches('a[href],button,input,select,textarea,summary,[tabindex]');

    if (!isNaturallyFocusable && !hadTabIndex) {
      trigger.setAttribute('tabindex', '0');
    }

    if (!isNaturallyFocusable && !hadRole) {
      trigger.setAttribute('role', 'button');
    }

    triggerRef.current = trigger;

    const handleClick = () => {
      suppressCloseFor(CLOSE_DELAY_MS);
      open();
    };

    trigger.addEventListener('mouseenter', open);
    trigger.addEventListener('mouseleave', close);
    trigger.addEventListener('focusin', open);
    trigger.addEventListener('focusout', close);
    trigger.addEventListener('click', handleClick);

    return () => {
      trigger.removeEventListener('mouseenter', open);
      trigger.removeEventListener('mouseleave', close);
      trigger.removeEventListener('focusin', open);
      trigger.removeEventListener('focusout', close);
      trigger.removeEventListener('click', handleClick);

      if (!isNaturallyFocusable && !hadTabIndex) {
        trigger.removeAttribute('tabindex');
      }

      if (!isNaturallyFocusable && !hadRole) {
        trigger.removeAttribute('role');
      }
    };
  }, [triggerId]);

  return { triggerRef, isOpen, open, close };
};
